import React, { useState, useEffect, useRef } from 'react';
import QRDisplay from '../Common/QRDisplay';
import { paymentService } from '../../services/payment';
import { cardService } from '../../services/cardService';
import { useAuth } from '../../hooks/useAuth';
import { Student, RFIDCard, CardTopUpRequest, PaymentResponse } from '../../types';

const QRTopUp: React.FC = () => {
  
  const [amount, setAmount] = useState<string>('');
  const [qrData, setQrData] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const { user } = useAuth();
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentCards, setStudentCards] = useState<RFIDCard[]>([]);
  const [transactionReference, setTransactionReference] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'failed' | 'timeout'>('pending');
  const PAYMENT_WINDOW = 300; // 5 minutes
  const QR_HIDE_TIME = 180; // 03 minutes
  const VERIFICATION_START = 20; // 0.5 minutes
  const SUPPORT_REPORT_ENABLE = 240; // 4 minutes
  // Timer and verification state
  //const [timeLeft, setTimeLeft] = useState<number>(180); // 3 minutes in seconds
  const [timeLeft, setTimeLeft] = useState<number>(PAYMENT_WINDOW);
  const [qrVisible, setQrVisible] = useState<boolean>(true);
  const [verificationActive, setVerificationActive] = useState<boolean>(false);
  
  // Refs for cleanup
  const verificationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const verificationStartTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const qrHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mainTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const paymentStatusRef = useRef(paymentStatus);
  const timeLeftRef = useRef(timeLeft);
  const transactionRefRef = useRef<string>('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  useEffect(() => {
    paymentStatusRef.current = paymentStatus;
  }, [paymentStatus]);

  useEffect(() => {
    timeLeftRef.current = timeLeft;
  }, [timeLeft]);

  useEffect(() => {
    transactionRefRef.current = transactionReference;
  }, [transactionReference]);
  // Load user's students on component mount
  useEffect(() => {
    const loadStudents = async () => {
      try {
        setStudentsLoading(true);
        if (user) {
          const userStudents = await cardService.getUserStudents();
          setStudents(userStudents);
          if (userStudents.length > 0) {
            setSelectedStudent(userStudents[0].id.toString());
            await loadStudentCards(userStudents[0].id);
          }
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          return;
        }
        setError('Failed to load users');
        console.error('Error loading users:', err);
      } finally {
        setStudentsLoading(false);
      }
    };

    loadStudents();
  }, [user]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, []);

  // Countdown timer effect
  // useEffect(() => {
  //   if (timeLeft > 0 && paymentStatus === 'pending' && qrData) {
  //     countdownIntervalRef.current = setTimeout(() => {
  //       setTimeLeft(prev => {
  //         const newTime = prev - 1;
  //         console.log(`[${formatTime(180 - newTime)}] Timer countdown: ${formatTime(newTime)} remaining`);
          
  //         // Check if we've reached the timeout
  //         if (newTime <= 0) {
  //           console.log('[03:00] Timer reached zero - triggering timeout');
  //           setPaymentStatus('timeout');
  //           setError('Payment timeout after 3 minutes. Please try again.');
  //           setQrVisible(false);
  //           clearAllTimers();
  //           setVerificationActive(false);
  //         }
          
  //         return newTime;
  //       });
  //     }, 1000);
      
  //     return () => {
  //       if (countdownIntervalRef.current) {
  //         clearTimeout(countdownIntervalRef.current);
  //       }
  //     };
  //   }
  // }, [timeLeft, paymentStatus, qrData]);
  useEffect(() => {
    if (timeLeft > 0 && paymentStatus === 'pending' && qrData) {
      countdownIntervalRef.current = setTimeout(() => {
        setTimeLeft(prev => {
          const newTime = prev - 1;
          console.log(`[Timer] Remaining: ${formatTime(newTime)}`);
          
          // Check if we've reached the timeout
          if (newTime <= 0) {
            console.log('[05:00] Timer reached zero - triggering timeout');
            setPaymentStatus('timeout');
            setError('Payment window expired. If you have already paid, please use "Report Payment Issue".');
            setSuccess('');
            setQrVisible(false);
            clearAllTimers();
            setVerificationActive(false);
            return 0;
          }
          
          return newTime;
        });
      }, 1000);
      
      return () => {
        if (countdownIntervalRef.current) {
          clearTimeout(countdownIntervalRef.current);
        }
      };
    }
  }, [timeLeft, paymentStatus, qrData]);

  // Clear all timers utility function
  const clearAllTimers = () => {
    console.log('Clearing all timers...');
    if (verificationIntervalRef.current) {
      clearInterval(verificationIntervalRef.current);
      verificationIntervalRef.current = null;
    }
    if (verificationStartTimeoutRef.current) {
      clearTimeout(verificationStartTimeoutRef.current);
      verificationStartTimeoutRef.current = null;
    }
    if (qrHideTimeoutRef.current) {
      clearTimeout(qrHideTimeoutRef.current);
      qrHideTimeoutRef.current = null;
    }
    if (mainTimeoutRef.current) {
      clearTimeout(mainTimeoutRef.current);
      mainTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearTimeout(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const loadStudentCards = async (studentId: number) => {
    try {
      const cards = await cardService.getStudentCards(studentId);
      setStudentCards(cards);
    } catch (err: any) {
      console.error('Error loading users cards:', err);
      setStudentCards([]);
    }
  };

  const selectedStudentData = students.find(s => s.id.toString() === selectedStudent);
  const activeCards = studentCards.filter(card => card.isActive && !card.isLost);
  const hasActiveCards = activeCards.length > 0;
  const currentCard = activeCards.find(card => card.id === selectedStudentData?.currentCardId);

 useEffect(() => {
  let cancelled = false;

  const run = async () => {
    handleReset();

    if (!selectedStudent) return;

    const cards = await cardService.getStudentCards(parseInt(selectedStudent));

    if (!cancelled) {
      setStudentCards(cards);
    }
  };

  run();

  return () => {
    cancelled = true;
  };
}, [selectedStudent]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string or valid number format
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value);
      setError('');
      setSuccess('');
    }
  };

  const handleStudentChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStudent(e.target.value);
  };

  const validateAmount = (): boolean => {
    const numAmount = parseFloat(amount);
    
    if (!amount || isNaN(numAmount)) {
      setError('Please enter a valid amount');
      return false;
    }
    
    if (numAmount <= 0) {
      setError('Amount must be greater than 0');
      return false;
    }
    
    if (numAmount < 500) {
      setError('Minimum top-up amount is PKR 500');
      return false;
    }
    
    if (numAmount > 5000) {
      setError('Maximum top-up amount is PKR 5,000');
      return false;
    }
    
    return true;
  };

  const verifyPaymentStatus = async (studentId: string, ref: string, amount: number) => {
    try {
      if (paymentStatusRef.current !== 'pending') return;
     
      console.log(`[${formatTime(PAYMENT_WINDOW - timeLeftRef.current)}] Verifying payment status...`)
      const result = await paymentService.verifyPayment(studentId, ref, amount);
      
      console.log('Payment verification result:', result);
      
      // Handle case-insensitive status checks
      const status = result.status?.toLowerCase();
      
      if (status === 'success' || status === 'completed') {
        console.log('Payment successful! Stopping all timers and refreshing...');
        setPaymentStatus('success');
        setSuccess('Payment successful! Refreshing page...');
        
        // Stop all timers immediately
        clearAllTimers();
        setVerificationActive(false);
        
        // Refresh the page after 2 seconds to update balance
        setTimeout(() => {
          console.log('Refreshing page...');
          window.location.reload();
        }, 2000);
        
      } else if (status === 'failed') {
        console.log('Payment failed. Stopping verification...');
        setPaymentStatus('failed');
        setError('Payment failed. Please try again.');
        clearAllTimers();
        setVerificationActive(false);
      } else {
        console.log(`Payment still pending with status: ${result.status}`);
      }
      // If still pending, continue verification (don't change anything)
    } catch (err: any) {
      console.error('Error verifying payment:', err);
      // Continue verification on error (network issues, etc.)
      // Don't stop the process for network errors
    }
  };

  const startPaymentWindow = (studentId: string, transactionRef: string, amount: number) => {
    console.log('Starting payment window...');

    // 🧹 CLEAN previous interval first
    if (verificationIntervalRef.current) {
      clearInterval(verificationIntervalRef.current);
      verificationIntervalRef.current = null;
    }

    setTimeout(() => {
      setVerificationActive(true);

      verificationIntervalRef.current = setInterval(() => {
        if (paymentStatusRef.current !== 'pending') return;

        verifyPaymentStatus(studentId, transactionRef, amount);
      }, 7000);

    }, VERIFICATION_START * 1000);

    qrHideTimeoutRef.current = setTimeout(() => {
      setQrVisible(false);

      // ⚠️ IMPORTANT: stop polling if UI closed
      // if (verificationIntervalRef.current) {
      //   clearInterval(verificationIntervalRef.current);
      //   verificationIntervalRef.current = null;
      // }

    }, QR_HIDE_TIME * 1000);
  };

  const handleGenerateQR = async () => {
    setError('');
    setSuccess('');
    setPaymentStatus('pending');
    setTimeLeft(PAYMENT_WINDOW);
    setQrVisible(true);
    setVerificationActive(false);
    
    if (!validateAmount()) {
      return;
    }

    if (!hasActiveCards || !currentCard) {
      setError('Selected users does not have an active card. Please manage cards first.');
      return;
    }
    
    if (!selectedStudentData) {
      setError('Please select a user');
      return;
    }
    
    setLoading(true);
    
    try {
      const numAmount = parseFloat(amount);
      
      const request: CardTopUpRequest = {
        amount: numAmount,
        merchantId: 'RFID_MERCHANT',
        cardId: currentCard.id,
        studentId: selectedStudentData.id,
        cardNumber: currentCard.cardNumber,
        studentName: selectedStudentData.name
      };

      const response: PaymentResponse = await paymentService.generateTopUpQR(request);
      
      console.log('QR Generation Response:', response);
      
      // Extract QR code data from the response structure
      const qrCodeData = response.qrCode || 
                         (response as any).qrCodeData || 
                         (response as any).data?.qrCode ||
                         (response as any).qr;
      
      if (!qrCodeData) {
        throw new Error('QR code data not found in response');
      }
      
      setQrData(qrCodeData);
      
      // Store transaction reference for verification
      const transactionRef = response.transactionId || 
                            (response as any).transactionReference ||
                            (response as any).data?.transactionReference;
      
      if (!transactionRef) {
        throw new Error('Transaction reference not found in response');
      }
      
      setTransactionReference(transactionRef);
      setSuccess('QR code generated successfully. You have limited time to scan and complete payment.');
      
      // Start the payment window process
      startPaymentWindow(selectedStudent, transactionRef, numAmount);
      
    } catch (err: any) {
      if (err.response?.status === 401) {
        return;
      }
      console.error('Error generating QR code:', err);
      setError(err.response?.data?.message || err.message || 'Failed to generate QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    console.log('Resetting payment window...');
    setAmount('');
    setQrData('');
    setError('');
    setSuccess('');
    setPaymentStatus('pending');
    setTransactionReference('');
    setTimeLeft(PAYMENT_WINDOW);
    setQrVisible(true);
    setVerificationActive(false);
    
    clearAllTimers();
  };

  const handleQuickAmount = (quickAmount: number) => {
    setAmount(quickAmount.toString());
    setError('');
    setSuccess('');
  };

  // Format time for display (MM:SS)
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get status message based on current state
  const handleReportPaymentIssue = async () => {
    if (!transactionReference || !selectedStudentData) return;

    setReportLoading(true);

    try {
      await paymentService.reportPaymentIssue({
        transactionReference,
        amount: parseFloat(amount),
        studentId: selectedStudentData.id,
        studentName: selectedStudentData.name,
        qrGeneratedAt: new Date().toISOString()
      });

      setReportSuccess(true);
      setSuccess('Issue reported. Support will review your payment.');
    } catch {
      setError('Failed to report issue. Please try again.');
    } finally {
      setReportLoading(false);
    }
  };
  const getStatusMessage = () => {
    if (paymentStatus === 'success') {
      return { text: 'Payment successful! Updating balance...', color: 'text-green-600', icon: 'success' };
    }

    if (paymentStatus === 'failed') {
      return { text: 'Payment failed. Please try again.', color: 'text-red-600', icon: 'error' };
    }

    if (paymentStatus === 'timeout') {
      return { text: 'Payment window expired. If you already paid, report below.', color: 'text-red-600', icon: 'error' };
    }

    // ⏱ Dynamic UX based on time
    if (timeLeft > 240) {
      return { text: 'Scan QR to complete payment', color: 'text-blue-600', icon: 'pending' };
    }

    if (timeLeft > 120) {
      return { text: 'Processing window active...', color: 'text-blue-500', icon: 'pending' };
    }

    if (timeLeft > 60) {
      return { text: 'Verifying payment...', color: 'text-yellow-600', icon: 'verifying' };
    }

    return { text: 'Finalizing... please wait', color: 'text-orange-600', icon: 'verifying' };
  };
  if (studentsLoading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Top Up Card
          </h2>
          <p className="text-sm text-gray-500">
            Secure QR-based instant wallet recharge
          </p>
        </div>

        {qrData && (
          <button
            onClick={handleReset}
            className="inline-flex items-center px-4 py-1 text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
          >
            New Transaction
          </button>
        )}
      </div>
     
      <div className="bg-white shadow rounded-lg p-2 md:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Left Column - Form */}
          <div className="space-y-6">
             <div className="mb-2 p-2 bg-blue-50 border border-blue-100 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-800 mb-1">
                Before you continue
              </h3>

              <ul className="text-xs text-blue-700 space-y-1 list-disc pl-5">
                <li>Ensure stable internet connection (avoid switching networks)</li>
                <li>Keep Bank/wallet app ready before scanning</li>
                <li>Do NOT refresh or close this page during payment</li>
                <li>Complete payment within 3 minutes after QR generation</li>
                <li>Wait for confirmation before retrying</li>
              </ul>
            </div>
            {/* Student Selection */}
            <div>
              <label htmlFor="user" className="block text-sm font-medium text-gray-700 mb-2">
                Select user
              </label>
              <select
                id="user"
                value={selectedStudent}
                onChange={handleStudentChange}
                disabled={loading || students.length === 0}
                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-3 border disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {students.length === 0 ? (
                  <option value="">No user available</option>
                ) : (
                  students.map(student => (
                    <option key={student.id} value={student.id.toString()}>
                      {student.name} ({student.studentId})
                    </option>
                  ))
                )}
              </select>
              
              {selectedStudentData && (
                <div className="mt-4 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-blue-900">
                      Current Balance:
                    </span>
                    <span className="text-lg font-semibold text-green-600">
                      PKR {selectedStudentData.balance.toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  
                  {currentCard && (
                    <div className="mt-1">
                      <p className="text-sm text-gray-600">
                        Current Card: {currentCard.cardNumber}
                      </p>
                    </div>
                  )}
                  
                  {!hasActiveCards && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-xs text-red-700 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        No active card. Please add a card in Card Management.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Amount Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter Top-Up Amount
                <span className="text-xs text-gray-400 ml-1">(PKR)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">PKR</span>
                </div>
                <input
                  type="text"
                  id="amount"
                  value={amount}
                  onChange={handleAmountChange}
                  disabled={loading || !hasActiveCards}
                  className={`pl-12 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border rounded-md p-3 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
              </div>
              
              <div className="mt-1 flex justify-between text-sm text-gray-500">
                <span>Min: PKR 500</span>
                <span>Max: PKR 5,000</span>
              </div>
              
              {error && (
                <p className="mt-1 text-sm text-red-600 flex items-center bg-red-50 p-2 rounded-md">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </p>
              )}
              
              {success && (
                <p className="mt-1 text-sm text-green-600 flex items-center bg-green-50 p-2 rounded-md">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {success}
                </p>
              )}
            </div>
            
            {/* Quick Amount Buttons */}
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Quick Select Amount:</p>
              <div className="grid grid-cols-4 gap-2 sm:gap-2">
                {[500, 1000, 2000,5000].map(quickAmount => (
                  <button
                    key={quickAmount}
                    onClick={() => handleQuickAmount(quickAmount)}
                    disabled={loading || !hasActiveCards}
                    className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-blue-50 hover:border-blue-300 transition"   >
                    PKR {quickAmount.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>
              <div className="bg-gray-50 rounded-md border p-4">
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">
                    Top-Up Amount
                  </span>

                  <span className="text-lg font-semibold text-blue-600">
                    PKR {amount || '0'}
                  </span>
                </div>
              </div>

            {/* Generate QR Button */}
            <div className="pt-2">
              <button
                onClick={handleGenerateQR}
                disabled={loading || !amount || !hasActiveCards}
                title={!hasActiveCards ? "No active card available" : ""}
                className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                   Generating QR...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0v1m-6-1h2M9 5h2m-2 0v1m0 6v1m6-6h2m-2 0v1m0 6v1m0 6h2m-2 0v-1M9 19h2m-2 0v-1m0-6v-1m0-6H9m2 0V5" />
                    </svg>
                    Generate Payment QR
                  </>
                )}
              </button>
            </div>

            {/* Debug Info */}
            {/* {qrData && (
              <div className="mt-4 p-3 bg-gray-50 rounded-md text-xs text-gray-500">
                <p><strong>Timer:</strong> {formatTime(timeLeft)} remaining</p>
                <p><strong>QR Visible:</strong> {qrVisible ? 'Yes' : 'No'}</p>
                <p><strong>Verification Active:</strong> {verificationActive ? 'Yes' : 'No'}</p>
                <p><strong>Status:</strong> {paymentStatus}</p>
              </div>
            )} */}
          </div>
          
          {/* Right Column - QR Code or Instructions */}
          <div className="flex flex-col">
            {qrData ? (
              <div className="flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-white rounded-lg p-6 h-full shadow-sm">
                <div className="text-center mb-2">
                    {qrVisible && qrData && (
                      <div className="bg-white p-4 rounded-lg shadow-sm mb-2">
                        <QRDisplay qrData={qrData} />
                      </div>
                    )}
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    {qrVisible ? 'Scan to Complete Payment' : 'Payment Window Closed'}
                  </h2>

                
                  {/* <p className="text-lg font-medium text-blue-600 mb-1">
                    PKR {parseFloat(amount).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-sm text-gray-500 mb-4">For: {selectedStudentData?.name}</p>
                   */}
                  {/* Countdown Timer */}
                  <div className="mb-2">
                   <div className="text-center">
                    <div className="text-xs text-gray-500 mb-1">Time remaining</div>
                    <div className={`text-3xl font-bold ${
                      timeLeft > 120 ? 'text-green-600' :
                      timeLeft > 60 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {formatTime(timeLeft)}
                    </div>
                  </div>
                  </div>
                  
                  {/* Status Message */}
                  <div className="mt-4 px-3 py-2 rounded-md bg-white shadow-sm">
                    {(() => {
                      const status = getStatusMessage();
                      return (
                        <div className={`flex items-center ${status.color}`}>
                          {status.icon === 'success' && (
                            <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                          {status.icon === 'error' && (
                            <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          )}
                          {(status.icon === 'verifying' || status.icon === 'pending') && (
                            <svg className="animate-spin h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          )}
                          <span className="text-sm font-medium">
                            {status.text}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                
              
                <p className="text-xs text-gray-500 mt-2">
                    Ref: {transactionReference}
                  </p>
                <div className="text-center">
                  <div className="flex items-center justify-center text-sm text-green-600 mb-2">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    Secure & Encrypted
                  </div>
                  <p className="text-xs text-gray-500 max-w-sm">
                    Balance will update automatically within 2-3 minutes after successful payment
                  </p>
                </div>
                {timeLeft <= SUPPORT_REPORT_ENABLE && paymentStatus !== 'success' && (
                  <div className="mt-4 w-full">
                    <button
                      onClick={handleReportPaymentIssue}
                      disabled={reportLoading || reportSuccess}
                      className="w-full px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md transition"
                    >
                      {reportLoading
                        ? 'Reporting...'
                        : reportSuccess
                        ? 'Reported ✓'
                        : 'Report Payment Issue'}
                    </button>

                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Use this if your amount was deducted but balance not updated
                    </p>
                  </div>
                )}
              </div>
              
            ) : (
              <div className="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-6 h-full">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                    <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0v1m-6-1h2M9 5h2m-2 0v1m0 6v1m6-6h2m-2 0v1m0 6v1m0 6h2m-2 0v-1M9 19h2m-2 0v-1m0-6v-1m0-6H9m2 0V5" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Top Up</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Select a user, enter amount, and generate QR code to make a secure payment
                  </p>
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          <strong>Supported Apps:</strong> Meezan Bank, UBL Digital, Alfalah, Easypaisa, JazzCash, and other RAAST-enabled apps
                        </p>
                      </div>
                    </div>
                  </div>
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
        <div className="flex">
          {/* Icon */}
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-yellow-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
            {/* Content */}
      <div className="ml-3 flex-1">
        <h4 className="text-sm font-medium text-yellow-800 text-center">
          Payment Window Timeline
        </h4>
        <div className="mt-2 text-sm text-yellow-700 flex justify-center">
          <ul className="list-disc list-outside space-y-1 inline-block text-left">
            <li><strong>0:00–3:00:</strong> QR code available</li>
            <li><strong>4:00:</strong> Report Issue available</li>
            <li><strong>5:00:</strong> Payment window expires</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
      </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  export default QRTopUp;